import {
  ArrowBack as BackIcon,
  Article as ArticleIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  LocationCity as CityIcon,
  OpenInNew as OpenIcon,
  Publish as PublishIcon,
  RateReview as ReviewIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/GoogleAuthContext';
import {
  api,
  ContentDetailItem,
  ContentStatus,
} from '../services/api-client';

const STATUS_COLORS: Record<ContentStatus, 'default' | 'warning' | 'info' | 'success' | 'error'> = {
  draft: 'default',
  in_review: 'warning',
  approved: 'info',
  scheduled: 'info',
  published: 'success',
};

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft',
  in_review: 'In Review',
  approved: 'Approved',
  scheduled: 'Scheduled',
  published: 'Published',
};

export default function ContentDetail() {
  const { contentType, slug } = useParams<{ contentType: string; slug: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [item, setItem] = useState<ContentDetailItem | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [feedback, setFeedback] = useState('');

  const canEdit = user?.role === 'admin' || user?.effective_permissions?.content?.edit;
  const canPublish = user?.role === 'admin' || user?.effective_permissions?.content?.publish;

  const fetchItem = async () => {
    if (!contentType || !slug) return;
    setLoading(true);
    setError(null);
    try {
      const data = await api.getContentItem(contentType, slug);
      setItem(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItem();
  }, [contentType, slug]);

  const handleSubmitReview = async () => {
    if (!item || !contentType || !slug) return;
    setActionLoading(true);
    try {
      await api.submitContentReview({ contentType, slug });
      await fetchItem();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!item || !contentType || !slug) return;
    setActionLoading(true);
    try {
      await api.approveContent({ contentType, slug, approved: true });
      await fetchItem();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    if (!item || !contentType || !slug) return;
    setActionLoading(true);
    try {
      await api.approveContent({ contentType, slug, approved: false, feedback });
      setRejectDialog(false);
      setFeedback('');
      await fetchItem();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!item || !contentType || !slug) return;
    setActionLoading(true);
    try {
      await api.publishContent({ contentType, slug });
      await fetchItem();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const status = item?.status as ContentStatus;
  const title = item?.title || item?.name || slug || '';
  const heroImage = item?.heroImage || (item?.heroImages?.[0]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !item) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/content')} sx={{ mb: 2 }}>
          Back to Content
        </Button>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!item) {
    return (
      <Box>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/content')} sx={{ mb: 2 }}>
          Back to Content
        </Button>
        <Alert severity="warning">Content not found</Alert>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <IconButton onClick={() => navigate('/content')}>
          <BackIcon />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight={600}>
            {title}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
            <Chip
              label={STATUS_LABELS[status] || status}
              color={STATUS_COLORS[status] || 'default'}
              size="small"
            />
            <Chip
              icon={contentType === 'editorial' ? <ArticleIcon /> : <CityIcon />}
              label={contentType}
              size="small"
              variant="outlined"
            />
            <Typography variant="caption" color="text.secondary">
              /{slug}
            </Typography>
          </Box>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Content Preview */}
        <Grid item xs={12} md={8}>
          {heroImage && (
            <Card sx={{ mb: 2 }}>
              <CardMedia
                component="img"
                height={300}
                image={heroImage}
                alt={title}
                sx={{ objectFit: 'cover' }}
              />
            </Card>
          )}

          {/* Editorial sections */}
          {contentType === 'editorial' && item.sections && item.sections.length > 0 && (
            <Paper sx={{ p: 3 }}>
              {item.subtitle && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                  {item.subtitle}
                </Typography>
              )}
              {item.sections.map((section, idx) => (
                <Box key={idx} sx={{ mb: 3 }}>
                  {section.heading && (
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                      {section.heading}
                    </Typography>
                  )}
                  {section.image && (
                    <Box
                      component="img"
                      src={section.image}
                      alt={section.heading || ''}
                      sx={{
                        width: '100%',
                        maxHeight: 300,
                        objectFit: 'cover',
                        borderRadius: 1,
                        mb: 2,
                      }}
                    />
                  )}
                  <Typography
                    variant="body2"
                    component="div"
                    sx={{
                      lineHeight: 1.8,
                      '& blockquote': {
                        borderLeft: 3,
                        borderColor: 'primary.main',
                        pl: 2,
                        ml: 0,
                        fontStyle: 'italic',
                        color: 'text.secondary',
                      },
                    }}
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                  {idx < item.sections!.length - 1 && <Divider sx={{ mt: 2 }} />}
                </Box>
              ))}
            </Paper>
          )}

          {/* City guide fields */}
          {contentType === 'city' && (
            <Paper sx={{ p: 3 }}>
              {item.subtitle && (
                <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
                  {item.subtitle}
                </Typography>
              )}
              {item.about?.description && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    About
                  </Typography>
                  <Typography variant="body2">{item.about.description}</Typography>
                </Box>
              )}
              {item.highlights && item.highlights.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Highlights ({item.highlights.length})
                  </Typography>
                  {item.highlights.map((h: any, idx: number) => (
                    <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
                      - {h.title}: {h.description}
                    </Typography>
                  ))}
                </Box>
              )}
              {item.stats && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    Stats
                  </Typography>
                  <Grid container spacing={1}>
                    {Object.entries(item.stats).map(([key, val]) =>
                      val ? (
                        <Grid item xs={6} sm={4} key={key}>
                          <Typography variant="caption" color="text.secondary">
                            {key}
                          </Typography>
                          <Typography variant="body2">
                            {Array.isArray(val) ? (val as string[]).join(', ') : String(val)}
                          </Typography>
                        </Grid>
                      ) : null
                    )}
                  </Grid>
                </Box>
              )}
            </Paper>
          )}
        </Grid>

        {/* Sidebar: Metadata + Actions */}
        <Grid item xs={12} md={4}>
          {/* Metadata */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Status
                  </Typography>
                  <Box>
                    <Chip
                      label={STATUS_LABELS[status] || status}
                      color={STATUS_COLORS[status] || 'default'}
                      size="small"
                    />
                  </Box>
                </Box>
                {item.author && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Author
                    </Typography>
                    <Typography variant="body2">{item.author}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Generated By
                  </Typography>
                  <Typography variant="body2">
                    {item.generatedBy === 'ai' ? 'AI Pipeline' : item.generatedBy || '—'}
                  </Typography>
                </Box>
                {item.approvedBy && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Approved By
                    </Typography>
                    <Typography variant="body2">{item.approvedBy}</Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Last Updated
                  </Typography>
                  <Typography variant="body2">{formatDate(item.updatedAt)}</Typography>
                </Box>
                {item.publishedAt && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Published
                    </Typography>
                    <Typography variant="body2">{formatDate(item.publishedAt)}</Typography>
                  </Box>
                )}
                {item.tags && item.tags.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Tags
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {item.tags.map((tag) => (
                        <Chip key={tag} label={tag} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </Box>
                )}
                {item.timeToRead && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Read Time
                    </Typography>
                    <Typography variant="body2">{item.timeToRead}</Typography>
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>
                Actions
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {status === 'draft' && canEdit && (
                  <Button
                    variant="contained"
                    startIcon={<ReviewIcon />}
                    onClick={handleSubmitReview}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Submit for Review
                  </Button>
                )}

                {status === 'in_review' && canPublish && (
                  <>
                    <Button
                      variant="contained"
                      color="success"
                      startIcon={<ApproveIcon />}
                      onClick={handleApprove}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Approve
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<RejectIcon />}
                      onClick={() => setRejectDialog(true)}
                      disabled={actionLoading}
                      fullWidth
                    >
                      Reject
                    </Button>
                  </>
                )}

                {status === 'approved' && canPublish && (
                  <Button
                    variant="contained"
                    color="primary"
                    startIcon={<PublishIcon />}
                    onClick={handlePublish}
                    disabled={actionLoading}
                    fullWidth
                  >
                    Publish Now
                  </Button>
                )}

                {status === 'published' && (
                  <Button
                    variant="outlined"
                    startIcon={<OpenIcon />}
                    href={
                      contentType === 'city'
                        ? `https://curated-content-platform-gamma.vercel.app/destinations/${slug}`
                        : `https://curated-content-platform-gamma.vercel.app/destinations/${slug}`
                    }
                    target="_blank"
                    fullWidth
                    disabled
                  >
                    View Live Page (Vercel not deployed)
                  </Button>
                )}

                {actionLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Reject Dialog */}
      <Dialog open={rejectDialog} onClose={() => setRejectDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Content</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Rejecting will move this content back to draft status. Provide feedback so the author
            can improve it.
          </Typography>
          <TextField
            autoFocus
            label="Feedback / Reason for Rejection"
            multiline
            rows={3}
            fullWidth
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleReject}
            disabled={actionLoading}
          >
            Reject
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
